/**
 * Tagebuch und Zusammenhänge auf dem Gerät.
 *
 * Gerechnet wird in `@kraftwerk/shared` (`contextBeforeSession`). Die Abfragen
 * stehen flach in einer Funktion — Dexies Live-Abfrage verliert hinter
 * verschachtelten `await` die Beobachtung (siehe CLAUDE.md).
 */

import { contextBeforeSession, newId } from '@kraftwerk/shared';
import type { MeasurementKey, Measurements, SessionContext } from '@kraftwerk/shared';
import { db } from './local.js';
import { updateProfileWeight } from './profile.js';
import type {
  KraftwerkDatabase,
  LocalJournalEntry,
  LocalJournalPhoto,
  LocalWorkoutSession,
} from './local.js';
import { stamp, writeRow } from './write.js';

export type JournalFields = Pick<
  LocalJournalEntry,
  | 'sleepHours'
  | 'sleepQuality'
  | 'energy'
  | 'mood'
  | 'soreness'
  | 'note'
  | 'bodyWeightKg'
  | 'measurements'
>;

/** Der Eintrag eines Tages — bei mehreren der zuletzt geänderte. */
export async function journalFor(
  date: string,
  database: KraftwerkDatabase = db,
): Promise<LocalJournalEntry | null> {
  const rows = (await database.journal_entries.where('date').equals(date).toArray()).filter(
    (row) => !row.deletedAt,
  );
  return rows.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0] ?? null;
}

/**
 * Felder eines Tages setzen. Gibt es den Eintrag noch nicht, entsteht er — mit
 * allen anderen Feldern leer, nicht 0.
 */
export async function saveJournal(
  date: string,
  changes: Partial<JournalFields>,
  database: KraftwerkDatabase = db,
): Promise<LocalJournalEntry> {
  const existing = await journalFor(date, database);
  const row: LocalJournalEntry = existing
    ? { ...existing, ...changes, updatedAt: stamp() }
    : {
        id: newId(),
        updatedAt: stamp(),
        deletedAt: null,
        date,
        sleepHours: null,
        sleepQuality: null,
        energy: null,
        mood: null,
        soreness: null,
        note: null,
        bodyWeightKg: null,
        measurements: null,
        ...changes,
      };
  return writeRow('journal_entries', row, 'upsert', database);
}

/**
 * Gewicht eines Tages speichern. Ist es der neueste Wiegetag, zieht das Profil
 * nach — ein nachgetragener Wert von letzter Woche ändert das Profil nicht.
 */
export async function saveBodyWeight(
  date: string,
  weightKg: number | null,
  database: KraftwerkDatabase = db,
): Promise<void> {
  await saveJournal(date, { bodyWeightKg: weightKg }, database);
  if (weightKg === null) return;
  const later = (await database.journal_entries.where('date').above(date).toArray()).some(
    (row) => !row.deletedAt && row.bodyWeightKg !== null && row.bodyWeightKg !== undefined,
  );
  if (!later) await updateProfileWeight(weightKg, database);
}

/**
 * Ein Körpermaß eines Tages setzen oder leeren. Die übrigen Maße des Tages
 * bleiben stehen; ist keins mehr übrig, wird die Karte `null`.
 */
export async function saveMeasurement(
  date: string,
  key: MeasurementKey,
  value: number | null,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await journalFor(date, database);
  const next: Measurements = { ...(existing?.measurements ?? {}) };
  if (value === null) delete next[key];
  else next[key] = value;
  await saveJournal(date, { measurements: Object.keys(next).length > 0 ? next : null }, database);
}

/** Alle Tage mit Körpermaßen — für den Verlauf. */
export async function listMeasurementDays(
  database: KraftwerkDatabase = db,
): Promise<{ date: string; measurements: Measurements }[]> {
  const rows = await database.journal_entries.toArray();
  return rows
    .filter((row) => !row.deletedAt && row.measurements)
    .map((row) => ({ date: row.date, measurements: row.measurements! }));
}

/** Alle Wiegetage — für Trend und Rückblick. */
export async function listBodyWeights(
  database: KraftwerkDatabase = db,
): Promise<{ date: string; kg: number }[]> {
  const rows = await database.journal_entries.toArray();
  const byDate = new Map<string, { kg: number; updatedAt: string }>();
  for (const row of rows) {
    if (row.deletedAt || row.bodyWeightKg === null || row.bodyWeightKg === undefined) continue;
    const known = byDate.get(row.date);
    if (!known || row.updatedAt > known.updatedAt) {
      byDate.set(row.date, { kg: row.bodyWeightKg, updatedAt: row.updatedAt });
    }
  }
  return [...byDate.entries()]
    .map(([date, value]) => ({ date, kg: value.kg }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** Was vor dieser Einheit war — Essen der 24 h davor, Schlaf, Energie. */
export async function contextForSession(
  session: Pick<LocalWorkoutSession, 'startedAt'>,
  timeZone: string,
  database: KraftwerkDatabase = db,
): Promise<SessionContext> {
  const start = Date.parse(session.startedAt);
  const from = new Date(start - 25 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const to = new Date(start + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  // Vorfilter über den Kalendertag-Index, genau rechnet `contextBeforeSession`.
  const meals = (
    await database.food_log.where('date').between(from, to, true, true).toArray()
  ).filter((row) => !row.deletedAt);
  const journal = (
    await database.journal_entries.where('date').between(from, to, true, true).toArray()
  ).filter((row) => !row.deletedAt);

  return contextBeforeSession({ startedAt: session.startedAt, timeZone, meals, journal });
}

export interface JournalDaySummary {
  entry: LocalJournalEntry;
  photos: number;
  /** Gedanken des Tages (E14) — Anzahl und der erste als Vorschau. */
  notes: number;
  firstNote: { title: string | null; body: string } | null;
}

/**
 * Die Tage mit Eintrag, neueste zuerst — je Tag der zuletzt geänderte.
 *
 * Flach abgefragt (siehe Kopf): zwei Tabellen, eine Funktion, kein Umweg.
 */
export async function listJournalDays(
  database: KraftwerkDatabase = db,
  limit = 90,
): Promise<JournalDaySummary[]> {
  const [entryRows, photoRows, noteRows] = await Promise.all([
    database.journal_entries.toArray(),
    database.journal_photos.toArray(),
    database.journal_notes.toArray(),
  ]);
  const entries = entryRows.filter((row) => !row.deletedAt);
  const photos = photoRows.filter((row) => !row.deletedAt);
  const notesPerDate = new Map<string, { title: string | null; body: string; time: string }[]>();
  for (const note of noteRows) {
    if (note.deletedAt) continue;
    const list = notesPerDate.get(note.date) ?? [];
    list.push(note);
    notesPerDate.set(note.date, list);
  }

  const latest = new Map<string, LocalJournalEntry>();
  for (const entry of entries) {
    const known = latest.get(entry.date);
    if (!known || entry.updatedAt > known.updatedAt) latest.set(entry.date, entry);
  }

  // Fotos zählen je Tag, nicht je Zeile: Zwei Geräte können denselben Tag mit
  // zwei Zeilen angelegt haben, die Fotos gehören trotzdem zum Tag.
  const dateOfEntry = new Map(entries.map((entry) => [entry.id, entry.date]));
  const photosPerDate = new Map<string, number>();
  for (const photo of photos) {
    const date = dateOfEntry.get(photo.journalEntryId);
    if (date) photosPerDate.set(date, (photosPerDate.get(date) ?? 0) + 1);
  }

  return [...latest.values()]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, limit)
    .map((entry) => {
      const notes = (notesPerDate.get(entry.date) ?? []).sort((a, b) =>
        a.time.localeCompare(b.time),
      );
      return {
        entry,
        photos: photosPerDate.get(entry.date) ?? 0,
        notes: notes.length,
        firstNote: notes[0] ? { title: notes[0].title, body: notes[0].body } : null,
      };
    });
}

/** Alle Fotos mit ihrem Tag, neueste Tage zuerst — für das Raster im Tagebuch. */
export async function listAllPhotos(
  database: KraftwerkDatabase = db,
): Promise<{ photo: LocalJournalPhoto; date: string }[]> {
  const entries = await database.journal_entries.toArray();
  const photos = (await database.journal_photos.toArray()).filter((row) => !row.deletedAt);
  const dateOfEntry = new Map(entries.map((entry) => [entry.id, entry.date]));
  return photos
    .flatMap((photo) => {
      const date = dateOfEntry.get(photo.journalEntryId);
      return date ? [{ photo, date }] : [];
    })
    .sort((a, b) => b.date.localeCompare(a.date) || b.photo.id.localeCompare(a.photo.id));
}
